import { View } from "@react-pdf/renderer";
import { styles } from "../reportingHelpers";
import { BoxscoreTableHeader } from "./BoxscoreTableHeader";
import { BoxScoreReportRow } from "./BoxScoreReportRow";
import React from "react";
import { TotalsBoxScoreReportRow } from "./TotalsBoxScoreReportRow";

export const TeamBoxscoreTable = ({ team, intl, roles, persons }) => {
  const efficiency = (s) => {
    return (
      4 * s.pointsThreeMade +
      3 * s.pointsTwoMade +
      2 * s.freeThrowsMade -
      s.pointsThreeAttempted -
      s.pointsTwoAttempted -
      s.freeThrowsAttempted +
      s.reboundsOffensive +
      s.reboundsDefensive +
      s.assists +
      s.steals +
      s.blocks -
      s.turnovers
    );
  };

  const plusMinus = (s) => s.points - s.pointsAgainst;

  const sumProps = (obj, initialAcc = {}) =>
    obj.reduce((acc, stats) => {
      for (const property in stats) {
        if (typeof stats[property] === "number") {
          if (acc[property]) {
            acc[property] = acc[property] + stats[property];
          } else {
            acc[property] = stats[property];
          }
        }
      }
      return acc;
    }, initialAcc);

  const teamStatsPeriods = team.teamStatsPeriods ?? [];

  const firstHalfStats = teamStatsPeriods.filter(
    (tS) => tS.periodId === 1 || tS.periodId === 2
  );
  const firstHalfStatsSummedProps = sumProps(firstHalfStats);
  const secondHalfStats = teamStatsPeriods.filter(
    (tS) => tS.periodId === 3 || tS.periodId === 4
  );
  const secondHalfStatsSummedProps = sumProps(secondHalfStats);
  const overtimeStats = teamStatsPeriods.filter((tS) => tS.periodId >= 11);
  const overtimeStatsSummedProps = sumProps(overtimeStats);

  return (
    <View style={{ ...styles.section, width: "215mm", marginBottom: 0 }}>
      <BoxscoreTableHeader {...{ intl, team, roles }} />
      {team.personStats &&
        team.personStats.map((r) => (
          <BoxScoreReportRow
            row={r}
            key={r.personId}
            intl={intl}
            persons={team.team.persons}
          />
        ))}
      {team.teamStats && team.teamStats.teamOrCoach && (
        <BoxScoreReportRow
          row={team.teamStats.teamOrCoach}
          isTeamCoachRow={true}
          intl={intl}
        />
      )}
      {team.teamStats && team.teamStats.totals && (
        <BoxScoreReportRow
          row={team.teamStats.totals}
          isTotalsRow={true}
          intl={intl}
          teamOrCoachRow={team.teamStats.teamOrCoach}
        />
      )}
      {team.personStats && (
        <>
          <TotalsBoxScoreReportRow
            row={team.personStats.filter((pS) => pS.starter)}
            personStats={team.personStats}
            teamStats={team.teamStats}
            teamStatsPeriods={teamStatsPeriods}
            intl={intl}
            rowLabel={intl.formatMessage({
              id: "reports.boxscore.table.gamestarters",
              defaultMessage: "Game Starters",
            })}
            efficiency={efficiency(
              sumProps(team.personStats.filter((pS) => pS.starter))
            )}
          />
          <TotalsBoxScoreReportRow
            row={team.personStats.filter((pS) => !pS.starter)}
            personStats={team.personStats}
            teamStats={team.teamStats}
            teamStatsPeriods={teamStatsPeriods}
            intl={intl}
            rowLabel={intl.formatMessage({
              id: "reports.boxscore.bench",
              defaultMessage: "Bench",
            })}
            efficiency={efficiency(
              sumProps(team.personStats.filter((pS) => !pS.starter))
            )}
          />
          <TotalsBoxScoreReportRow
            row={firstHalfStats.map((stat) => stat.totals)}
            personStats={team.personStats}
            teamStats={team.teamStats}
            teamStatsPeriods={teamStatsPeriods}
            intl={intl}
            rowLabel={intl.formatMessage({
              id: "reports.boxscore.1qt2qt",
              defaultMessage: "1QT 2QT",
            })}
            efficiency={efficiency(firstHalfStatsSummedProps)}
            plusMinus={plusMinus(firstHalfStatsSummedProps)}
          />
          <TotalsBoxScoreReportRow
            row={secondHalfStats.map((stat) => stat.totals)}
            personStats={team.personStats}
            teamStats={team.teamStats}
            teamStatsPeriods={teamStatsPeriods}
            intl={intl}
            rowLabel={intl.formatMessage({
              id: "reports.boxscore.3qt4qt",
              defaultMessage: "3QT 4QT",
            })}
            efficiency={efficiency(secondHalfStatsSummedProps)}
            plusMinus={plusMinus(secondHalfStatsSummedProps)}
          />
          <TotalsBoxScoreReportRow
            row={overtimeStats.map((stat) => stat.totals)}
            personStats={team.personStats}
            teamStats={team.teamStats}
            teamStatsPeriods={teamStatsPeriods}
            intl={intl}
            rowLabel={intl.formatMessage({
              id: "reports.scoresheet.overtime",
              defaultMessage: "Overtime",
            })}
            efficiency={efficiency(overtimeStatsSummedProps)}
            plusMinus={plusMinus(overtimeStatsSummedProps)}
          />
        </>
      )}
    </View>
  );
};
